import { asyncTween, directionalVelocity, gridMove, wait, StateMachine, WaitState, wouldHit } from 'tower/util';
import BattleScene from 'tower/scenes/battle';
import Enemy, { EnemyState } from 'tower/scenes/battle/enemy';
import Projectile from './projectile';
import * as C from 'tower/constants';

const PUSH_DELAY_MS = 400;

interface StatueOptions {
  color?: 'grey' | 'red';
  facing?: 'left' | 'right';
  pushable?: boolean;
  disabled?: boolean;
  hp?: number;
  delay?: number | [number, number];
}

export default class Statue extends Enemy {
  pushTime: number | null = null;
  pushed = false;

  constructor(
    scene: BattleScene,
    x: number,
    y: number,
    public statueOptions: StatueOptions = {},
  ) {
    let frame = 230;
    frame += statueOptions.color === 'red' ? 16 : 0;
    frame += statueOptions.facing === 'right' ? 0 : 1;

    super(scene, x, y, 'enemies', frame, 'statue', {
      hideSpawn: true,
      turnable: false,
      solid: true,
      hp: statueOptions.hp,
      knockback: false,
    });
    this.body.setSize(16, 16);

    if (statueOptions.delay !== undefined) {
      (this.stateMachine.possibleStates.wait as WaitState).delay = statueOptions.delay;
    }
  }

  makeStateMachine(scene: BattleScene) {
    return new StateMachine('wait', {
      wait: new WaitState([160, 170], 'shoot'),
      shoot: new StatueShootingState(scene, this),
      pushed: new StatuePushedState(scene, this),
    });
  }

  disable() {
    this.statueOptions.disabled = true;
  }

  update() {
    if (this.statueOptions.disabled) {
      return;
    }

    super.update();

    // Must be pushable and unpushed
    if (!this.statueOptions.pushable || this.pushed) {
      return;
    }

    // Player must be holding down a direction
    const { scene } = this;
    if (
      ![scene.controller.up, scene.controller.down, scene.controller.left, scene.controller.right].some(
        (button) => button.isDown,
      )
    ) {
      this.pushTime = null;
      return;
    }

    // Can only push if we're touching
    const { player } = scene;
    if (!wouldHit(scene, player, this, ...directionalVelocity(4, player.direction))) {
      this.pushTime = null;
      return;
    }

    // Is there a wall blocking the opposite side?
    const walls = scene.currentRoom.maze.getWallsAtWorldXY(this.x, this.y);
    if (walls[player.direction]) {
      this.pushTime = null;
      return;
    }

    // Did we just start pushing?
    if (this.pushTime === null) {
      this.pushTime = scene.time.now;
      return;
    }

    // Have we pushed long enough?
    if (scene.time.now - this.pushTime > PUSH_DELAY_MS) {
      this.pushed = true;
      this.stateMachine.transition('pushed');
    }
  }
}

class StatueState extends EnemyState {
  declare enemy: Statue;
}

class StatueShootingState extends StatueState {
  async handleEntered() {
    const { scene, enemy } = this;
    const fireball = new Projectile(
      scene,
      enemy.x + (enemy.statueOptions.facing === 'left' ? -8 : 8),
      enemy.y,
      'fireball',
      enemy.direction,
      { target: 'player' },
    );
    fireball.body.setVelocity(0);
    await wait(scene, 200);

    if (!fireball.isDestroyed) {
      scene.physics.moveToObject(fireball, scene.player, C.FIREBALL_SPEED);
    }
    return this.transition('wait');
  }
}

class StatuePushedState extends StatueState {
  async handleEntered() {
    const { scene, enemy } = this;
    scene.events.emit('statuepushed', enemy);

    const [nx, ny] = gridMove(enemy.x, enemy.y, scene.player.direction, 24);
    await asyncTween(scene, {
      x: nx,
      y: ny,
      targets: [enemy],
      duration: 500,
    });

    return this.transition('wait');
  }
}
